import React from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { t } from '../../utils/translations';

const Breadcrumbs = ({ items = [], className = '' }) => {
  const handleNavigation = (path) => {
    if (path) {
      window.location.href = path;
    }
  };

  const generateBreadcrumbs = () => {
    const currentPath = window.location?.pathname;
    const pathSegments = currentPath?.split('/')?.filter(Boolean);
    
    // Default breadcrumbs based on current path
    const defaultBreadcrumbs = [
      { label: t('breadcrumbs.dashboard'), path: '/dashboard', icon: 'Home' }
    ];

    // Route-specific breadcrumb mappings
    const routeMappings = {
      'cities-management': { label: t('breadcrumbs.citiesManagement'), icon: 'MapPin' },
      'sub-unions-management': { label: t('breadcrumbs.subUnionsManagement'), icon: 'Building2' },
      'sub-union-form': { label: t('breadcrumbs.subUnionForm'), icon: 'Plus', parent: '/sub-unions-management' },
      'articles-management': { label: t('breadcrumbs.articlesManagement'), icon: 'FileText' },
      'article-editor': { label: t('breadcrumbs.articleEditor'), icon: 'Edit3', parent: '/articles-management' },
      'projects-management': { label: t('breadcrumbs.projectsManagement'), icon: 'FolderOpen' },
      'project-form': { label: t('breadcrumbs.projectForm'), icon: 'Plus', parent: '/projects-management' },
      'offices-management': { label: t('breadcrumbs.officesManagement'), icon: 'Briefcase' },
      'office-form': { label: t('breadcrumbs.officeForm'), icon: 'Plus', parent: '/offices-management' },
      'settings': { label: t('breadcrumbs.settings'), icon: 'Settings' },
      'help': { label: t('breadcrumbs.help'), icon: 'HelpCircle' },
      'profile': { label: t('breadcrumbs.profile'), icon: 'User' },
    };

    if (currentPath === '/dashboard') {
      return [{ label: t('breadcrumbs.dashboard'), path: '/dashboard', icon: 'Home', isActive: true }];
    }

    const breadcrumbs = [...defaultBreadcrumbs];
    
    pathSegments?.forEach((segment, index) => {
      const mapping = routeMappings?.[segment];
      if (mapping) {
        // Add parent if exists and not already in breadcrumbs
        if (mapping?.parent && !breadcrumbs?.find(b => b?.path === mapping?.parent)) {
          const parentSegment = mapping?.parent?.split('/')?.pop();
          const parentMapping = routeMappings?.[parentSegment];
          if (parentMapping) {
            breadcrumbs?.push({
              label: parentMapping?.label,
              path: mapping?.parent,
              icon: parentMapping?.icon
            });
          }
        }

        // Add current segment
        const isLast = index === pathSegments?.length - 1;
        breadcrumbs?.push({
          label: mapping?.label,
          path: isLast ? null : `/${pathSegments?.slice(0, index + 1)?.join('/')}`,
          icon: mapping?.icon,
          isActive: isLast
        });
      }
    });

    return breadcrumbs;
  };

  const breadcrumbItems = items?.length > 0 ? items : generateBreadcrumbs();

  if (breadcrumbItems?.length <= 1) {
    return null;
  }

  return (
    <nav className={`flex items-center space-x-2 space-x-reverse text-sm ${className}`} aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2 space-x-reverse">
        {breadcrumbItems?.map((item, index) => {
          const isLast = index === breadcrumbItems?.length - 1;
          const isActive = item?.isActive || isLast;

          return (
            <li key={index} className="flex items-center space-x-2 space-x-reverse">
              {index > 0 && (
                <Icon 
                  name="ChevronLeft" 
                  size={14} 
                  className="text-muted-foreground flex-shrink-0" 
                />
              )}
              {isActive ? (
                <span className="flex items-center space-x-2 text-foreground font-medium">
                  {item?.icon && (
                    <Icon name={item?.icon} size={16} className="text-primary" />
                  )}
                  <span>{item?.label}</span>
                </span>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => handleNavigation(item?.path)}
                >
                  <div className="flex items-center space-x-2 space-x-reverse">
                    {item?.icon && (
                      <Icon name={item?.icon} size={16} />
                    )}
                    <span>{item?.label}</span>
                  </div>
                </Button>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;